<!--
  订单跟进记录的添加 / 编辑表单弹窗

  原型字段：*项目名称（下拉）、跟进方式（下拉）、*跟进内容、开发汇总、Bug汇总、
           今日工作内容、下一步工作计划、待处理事项、*跟进人（只读）、跟进时间（只读）

  ★ 跟进人 / 跟进时间 / 单号 都由后端生成或兜底，表单只做只读展示。
-->
<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="820px">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="130px"
    >
      <el-row>
        <el-col :span="12">
          <el-form-item label="项目名称" prop="orderId">
            <el-select
              v-model="formData.orderId"
              class="w-1/1"
              clearable
              filterable
              placeholder="请选择项目"
            >
              <el-option
                v-for="item in orderOptions"
                :key="item.id"
                :label="item.projectName"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="跟进方式" prop="type">
            <el-select v-model="formData.type" class="w-1/1" clearable placeholder="请选择跟进方式">
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_FOLLOW_UP_TYPE)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="跟进内容" prop="content">
            <el-input
              v-model="formData.content"
              :rows="5"
              placeholder="请输入跟进内容"
              type="textarea"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="开发汇总" prop="devSummary">
            <el-input
              v-model="formData.devSummary"
              :rows="5"
              placeholder="请输入开发汇总"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="Bug汇总" prop="bugSummary">
            <el-input
              v-model="formData.bugSummary"
              :rows="5"
              placeholder="请输入 Bug 汇总"
              type="textarea"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="今日工作内容" prop="todayWork">
            <el-input
              v-model="formData.todayWork"
              :rows="5"
              placeholder="请输入今日工作内容"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="下一步工作计划" prop="nextPlan">
            <el-input
              v-model="formData.nextPlan"
              :rows="5"
              placeholder="请输入下一步工作计划"
              type="textarea"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="待处理事项" prop="pendingMatter">
            <el-input
              v-model="formData.pendingMatter"
              :rows="5"
              placeholder="请输入待处理事项"
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="跟进人">
            <el-input v-model="followerName" disabled placeholder="跟进人" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="跟进时间">
            <el-input v-model="followTimeText" disabled placeholder="跟进时间" />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">提 交</el-button>
      <el-button @click="dialogVisible = false">结 束</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { OrderFollowUpApi } from '@/api/crm/orderFollowUp'
import * as OrderApi from '@/api/crm/order'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { formatDate } from '@/utils/formatTime'
import { useUserStore } from '@/store/modules/user'

defineOptions({ name: 'OrderFollowUpForm' })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗
const userStore = useUserStore()

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const orderOptions = ref<any[]>([]) // 项目（订单）下拉
const followerName = ref('') // 跟进人名称（只读）
const followTimeText = ref('') // 跟进时间（只读）

const formData = ref<any>({
  id: undefined,
  orderId: undefined,
  type: undefined,
  content: undefined,
  devSummary: undefined,
  bugSummary: undefined,
  todayWork: undefined,
  nextPlan: undefined,
  pendingMatter: undefined
})
const formRules = reactive({
  orderId: [{ required: true, message: '项目名称不能为空', trigger: 'change' }],
  content: [{ required: true, message: '跟进内容不能为空', trigger: 'blur' }]
})
const formRef = ref() // 表单 Ref

/** 打开弹窗 */
const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = type === 'create' ? '新增订单跟进记录' : '编辑订单跟进记录'
  formType.value = type
  resetForm()
  // 加载项目下拉
  formLoading.value = true
  try {
    orderOptions.value = (await OrderApi.getOrderSimpleList()) || []
    // 修改时，加载数据
    if (id) {
      const data = await OrderFollowUpApi.getOrderFollowUp(id)
      formData.value = {
        id: data.id,
        orderId: data.orderId,
        type: data.type,
        content: data.content,
        devSummary: data.devSummary,
        bugSummary: data.bugSummary,
        todayWork: data.todayWork,
        nextPlan: data.nextPlan,
        pendingMatter: data.pendingMatter
      }
      followerName.value = data.followerUserName || ''
      followTimeText.value = formatDate(data.followTime)
    } else {
      // 新增时：跟进人 / 跟进时间 只读显示「当前登录用户 / 当下」
      followerName.value = userStore.getUser?.nickname || ''
      followTimeText.value = formatDate(Date.now())
    }
  } finally {
    formLoading.value = false
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  await formRef.value.validate()
  // 提交请求
  formLoading.value = true
  try {
    const data = { ...formData.value }
    if (formType.value === 'create') {
      await OrderFollowUpApi.createOrderFollowUp(data)
      message.success(t('common.createSuccess'))
    } else {
      await OrderFollowUpApi.updateOrderFollowUp(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    orderId: undefined,
    type: undefined,
    content: undefined,
    devSummary: undefined,
    bugSummary: undefined,
    todayWork: undefined,
    nextPlan: undefined,
    pendingMatter: undefined
  }
  orderOptions.value = []
  followerName.value = ''
  followTimeText.value = ''
  formRef.value?.resetFields()
}
</script>
